import type { Profile, Rule, Input } from "./engine/types";
const policy = { text: "space-presentation", urls: "conservative" } as const;
const article = {
  types: ["Article", "BlogPosting", "NewsArticle"],
  by: "page_url",
} as const;
const common = {
  expected: true,
  scope: 'article[data-template="journal-v1"]',
  source: "text",
  absence: "undetermined",
  ambiguity: "undetermined",
} as const;
export const demoProfile: Profile = {
  schemaVersion: "1.0",
  id: "journal-demo",
  version: "1.0.0",
  language: "fr",
  timezone: "Europe/Paris",
  referenceDate: "2026-10-15",
  marker: 'article[data-template="journal-v1"]',
  pageUrl: { selector: 'link[rel="canonical"]', attribute: "href" },
  rules: [
    {
      ...common,
      id: "published",
      label: "Date de publication",
      reader: "date",
      selector: ".published",
      cardinality: "one",
      entity: { ...article, types: [...article.types] },
      property: "datePublished",
      policy: { ...policy, dateFormat: "fr" },
    },
    {
      ...common,
      id: "modified",
      label: "Date de révision",
      reader: "date",
      selector: ".modified",
      cardinality: "one",
      entity: { ...article, types: [...article.types] },
      property: "dateModified",
      policy: { ...policy, dateFormat: "fr" },
    },
    {
      ...common,
      id: "authors",
      label: "Auteurs",
      reader: "authors",
      selector: ".author",
      cardinality: "set",
      entity: { ...article, types: [...article.types] },
      property: "author",
      fields: { name: ".author-name", url: "a" },
      policy: { ...policy, order: "set" },
    },
    {
      ...common,
      id: "breadcrumb",
      label: "Fil d’Ariane",
      reader: "breadcrumb",
      selector: ".breadcrumb li",
      cardinality: "sequence",
      entity: { types: ["BreadcrumbList"], by: "id", id: "#breadcrumb" },
      property: "itemListElement",
      fields: { name: ".crumb-label", url: "a" },
      policy: { ...policy, lastUnlinked: "page_url" },
    },
    {
      ...common,
      id: "faq",
      label: "Questions et réponses",
      reader: "faq",
      selector: ".faq details",
      cardinality: "set",
      entity: { types: ["FAQPage"], by: "id", id: "#faq" },
      property: "mainEntity",
      fields: {
        question: "summary",
        answer: ".answer",
        keyAttribute: "data-key",
      },
      policy: { ...policy, faqKey: "identifier", links: true },
    },
  ],
};
export type RecordData = {
  id: string;
  url: string;
  lang: string;
  title: string;
  body: string;
  published: string;
  modified: string;
  authors: { name: string; url: string }[];
  breadcrumb: { name: string; url: string | null }[];
  faq: { id: string; question: string; answer: string }[];
  revision: number;
};
export const initial: RecordData = {
  id: "article-001",
  url: "https://atelier.example/journal/assistance",
  lang: "fr",
  title: "Un support qui tient parole",
  body: "Notre équipe vous accompagne du lundi au vendredi.",
  published: "2026-09-01",
  modified: "2026-09-18",
  authors: [
    {
      name: "Élise Marceau",
      url: "https://atelier.example/equipe/elise-marceau",
    },
  ],
  breadcrumb: [
    { name: "Journal", url: "https://atelier.example/journal/" },
    { name: "Accompagnement", url: "https://atelier.example/accompagnement/" },
    { name: "Un support qui tient parole", url: null },
  ],
  faq: [
    {
      id: "weekend",
      question: "Le support est-il ouvert le samedi ?",
      answer: "Le support est disponible le samedi.",
    },
    {
      id: "delai",
      question: "Quel est le délai de réponse ?",
      answer: "Une réponse est apportée sous 2 jours ouvrés.",
    },
  ],
  revision: 1,
};
export const escapeHTML = (s: unknown) =>
  String(s).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ]!,
  );
export const jsonForHTML = (v: unknown) =>
  JSON.stringify(v, null, 2)
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
export function jsonGenerator(r: RecordData) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "@id": r.url + "#article",
        url: r.url,
        mainEntityOfPage: { "@id": r.url },
        headline: r.title,
        datePublished: r.published,
        dateModified: r.modified,
        author: r.authors.map((a, i) => ({ "@id": r.url + `#author-${i}` })),
      },
      ...r.authors.map((a, i) => ({
        "@type": "Person",
        "@id": r.url + `#author-${i}`,
        name: a.name,
        url: a.url,
      })),
      {
        "@type": "BreadcrumbList",
        "@id": r.url + "#breadcrumb",
        itemListElement: r.breadcrumb.map((v, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: v.name,
          ...(v.url ? { item: v.url } : {}),
        })),
      },
      {
        "@type": "FAQPage",
        "@id": r.url + "#faq",
        mainEntity: r.faq.map((v) => ({
          "@type": "Question",
          identifier: v.id,
          name: v.question,
          acceptedAnswer: { "@type": "Answer", text: v.answer },
        })),
      },
    ],
  };
}
export function htmlGenerator(r: RecordData): string {
  return `<article data-template="journal-v1" data-revision="${r.revision}"><nav aria-label="Fil d’Ariane"><ol class="breadcrumb">${r.breadcrumb.map((v) => `<li>${v.url ? `<a href="${escapeHTML(v.url)}">` : ""}<span class="crumb-label">${escapeHTML(v.name)}</span>${v.url ? "</a>" : ""}</li>`).join("")}</ol></nav><h1>${escapeHTML(r.title)}</h1><p>${escapeHTML(r.body)}</p><p>Publié le <time class="published" datetime="${r.published}">${r.published}</time> · Révisé le <time class="modified" datetime="${r.modified}">${r.modified}</time></p><div class="authors">${r.authors.map((a) => `<p class="author"><a href="${escapeHTML(a.url)}"><span class="author-name">${escapeHTML(a.name)}</span></a></p>`).join("")}</div><section class="faq"><h2>Questions fréquentes</h2>${r.faq.map((q) => `<details data-key="${escapeHTML(q.id)}"><summary>${escapeHTML(q.question)}</summary><p class="answer">${escapeHTML(q.answer)}</p></details>`).join("")}</section></article>`;
}
export function documentGenerator(
  htmlRecord: RecordData,
  jsonRecord = htmlRecord,
): string {
  return `<!doctype html><html lang="${htmlRecord.lang}"><head><meta charset="utf-8"><title>${escapeHTML(htmlRecord.title)}</title><link rel="canonical" href="${escapeHTML(htmlRecord.url)}"><script type="application/ld+json">${jsonForHTML(jsonGenerator(jsonRecord))}</script></head><body>${htmlGenerator(htmlRecord)}</body></html>`;
}
export const scenarios = [
  {
    id: "S0",
    name: "Avant toute modification",
    action: "Revenir à la révision initiale",
    field: "modified",
    explanation:
      "Deux architectures, un même article. Chaque contrôle du profil est exécuté.",
    lesson:
      "Les deux sorties concordent sur le profil. Leur exactitude reste une question éditoriale.",
  },
  {
    id: "S1",
    name: "Une vraie révision",
    action: "Corriger l’article et dater la révision",
    field: "modified",
    explanation:
      "Le contenu a été corrigé le 2 octobre. La date affichée avance ; la copie JSON-LD séparée reste au 18 septembre.",
    lesson:
      "Une source commune transmet la révision aux deux générateurs. Un JSON valide peut pourtant décrire une ancienne version.",
  },
  {
    id: "S2",
    name: "Un nouveau déploiement",
    action: "Simuler un nouveau build",
    field: "modified",
    explanation:
      "Le build passe au 4 octobre. Aucune révision éditoriale n’a eu lieu.",
    lesson:
      "Une date de déploiement ne prouve pas une modification du contenu. La date de publication reste stable.",
  },
  {
    id: "S3",
    name: "Un nouvel auteur",
    action: "Remplacer l’autrice et son profil",
    field: "authors.names",
    explanation:
      "Élise Marceau est remplacée par Hugo Delmas. Le nom et la destination du profil changent ensemble.",
    lesson:
      "Le moteur distingue noms, URL de profil et références locales. Il ne vérifie ni identité réelle ni expertise.",
  },
  {
    id: "S4",
    name: "Une réponse corrigée",
    action: "Corriger la disponibilité du samedi",
    field: "faq",
    explanation:
      "Une négation est ajoutée : le support n’est pas disponible le samedi.",
    lesson:
      "Une petite différence peut changer le sens. Le contrôle conserve les négations et ignore seulement la présentation et les espaces.",
  },
  {
    id: "S5",
    name: "Une autre rubrique",
    action: "Déplacer l’article dans Services",
    field: "breadcrumb",
    explanation:
      "La rubrique Accompagnement devient Services. Son lien change aussi.",
    lesson:
      "Les libellés et destinations sont comparés dans l’ordre déclaré, sans reconstruire une hiérarchie à partir de l’URL.",
  },
  {
    id: "S6",
    name: "Même valeur, mauvaise information",
    action: "Appliquer la date de build aux deux sorties",
    field: "modified",
    explanation:
      "Le 4 octobre apparaît dans le HTML et le JSON-LD, sans nouvelle révision du texte.",
    lesson:
      "Concordant ne signifie pas vrai. Le journal éditorial de cette fixture, indépendant du moteur, situe la dernière révision au 18 septembre.",
  },
  {
    id: "S7",
    name: "Un cache décalé",
    action: "Assembler HTML v2 et JSON-LD v1",
    field: "modified",
    explanation:
      "Le HTML provient de la révision 2 ; le script JSON-LD a été conservé en cache depuis la révision 1.",
    lesson:
      "Même avec une source commune, l’assemblage de versions différentes recrée un écart. Le cache fait partie de la chaîne.",
  },
];
export function scenario(id: string) {
  const info = scenarios.find((s) => s.id === id) || scenarios[1];
  const html = structuredClone(initial);
  let a = structuredClone(initial),
    b = html;
  if (["S1", "S7"].includes(info.id)) {
    html.modified = "2026-10-02";
    html.body =
      "Notre équipe vous accompagne du lundi au vendredi, de 9 h à 18 h.";
    html.revision = 2;
  }
  if (info.id === "S2") a.modified = "2026-10-04";
  if (info.id === "S3") {
    html.authors = [
      {
        name: "Hugo Delmas",
        url: "https://atelier.example/equipe/hugo-delmas",
      },
    ];
    html.revision = 2;
  }
  if (info.id === "S4") {
    html.faq[0].answer = "Le support n’est pas disponible le samedi.";
    html.revision = 2;
  }
  if (info.id === "S5") {
    html.breadcrumb[1] = {
      name: "Services",
      url: "https://atelier.example/services/",
    };
    html.revision = 2;
  }
  if (info.id === "S6") {
    html.modified = "2026-10-04";
    a = structuredClone(html);
  }
  if (info.id === "S7") b = structuredClone(initial);
  const inputs: Input[] = [
    {
      name: `${info.id}-saisies-separees.html`,
      html: documentGenerator(html, a),
      mode: "html_source",
      provenance: "Fixture fictive générée : architecture A",
    },
    {
      name: `${info.id}-source-commune.html`,
      html: documentGenerator(html, b),
      mode: "html_source",
      provenance: "Fixture fictive générée : architecture B",
    },
  ];
  return {
    info,
    htmlRecord: html,
    aRecord: a,
    bRecord: b,
    inputs,
    editorialJournal: {
      lastRealRevision:
        info.id === "S1" || info.id === "S7" ? "2026-10-02" : "2026-09-18",
      buildDate: "2026-10-04",
    },
  };
}
